import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  HStack,
  Code,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Util } from '@finley-technologies/finley-types';

export type MeshRunTableRowType = {
  meshRunId: string;
  status: string;
  startedAt: Date;
  reportingDate: Util.FullDateStr;
  version: number;
};

const MeshRunIdCell = ({ meshRunId }: { meshRunId: string }) => {
  return (
    <HStack spacing={4}>
      <Code>
        <Link
          to={`../details/${meshRunId}`}
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          Details
        </Link>
      </Code>
    </HStack>
  );
};

const columns: ColumnDef<MeshRunTableRowType>[] = [
  {
    accessorKey: 'meshRunId',
    header: 'ID',
    cell: function meshRunIdCell({ getValue }) {
      return <MeshRunIdCell meshRunId={getValue() as string} />;
    },
  },
  {
    accessorKey: 'startedAt',
    header: 'Started At',
    cell: (info) => new Date(info.getValue() as Date).toLocaleString(),
  },
  {
    accessorKey: 'status',
    header: 'Status',
  },
  {
    accessorKey: 'reportingDate',
    header: 'Reporting Date',
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'version',
    header: 'Version',
  },
];

export const MeshTable = ({ data }: { data: MeshRunTableRowType[] }) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable<MeshRunTableRowType>({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box overflowX="auto">
      <Table variant="striped">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
